import { Box, Text, ToastType } from '@bechtel/gds';
import { Cable, ConstructionStatus, OperatorType, Question, UiMessageKey, UiTextEnum } from '@bechtel/sr-types';
import { setNestedObjectValues, useFormikContext } from 'formik';
import { defaultTo, filter, find, get, has, includes, isEmpty, map, some } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import {
  CommodityFooter,
  FormField,
  FormSectionHeading,
  QuestionModalResponse,
  SelectFieldComponent,
  TextFieldComponent,
} from '~/components';
import MessageModal from '~/components/organisms/message-modal/message';
import { useDebounce, usePermissions, useSelectListQuery, useToast } from '~/hooks';
import { FeedbackCardStyled, FeedbackMainContainerStyled, FormContainerStyled } from '../../styled';
import { CableReels } from './form-components/cable-reels';
import { Main } from './form-components/main';
import { CableFeedbackFormikState } from './types';

const SelectedItemComponent = (items) => {
  return <Text>{items.item.column2}</Text>;
};

const ItemComponent = (items) => {
  return <Text>{items.item.column2}</Text>;
};

const CableFeedbackForm = ({
  searchCables,
  searchCableloading,
  searchCableReelsLoading,
  searchCableReels,
  cableData,
  cableReelTableMetadata,
  feedbackCableLoading,
  initialValues,
  setStatusCheck,
  statusCheck,
  errorKeys,
  saveHandler,
  formikValuesRef,
  errorKeysCheck,
  setErrorKeysCheck,
  setInitialValues,
  cableTableMetadata,
}) => {
  const { showToast } = useToast();
  const {
    setFieldValue,
    values,
    handleSubmit,
    handleReset,
    dirty,
    isSubmitting,
    touched,
    resetForm,
    setFieldTouched,
    validateForm,
    setTouched,
  } = useFormikContext<CableFeedbackFormikState>();
  const [getSelectListData, { data }] = useSelectListQuery('CABLE.CONSTRUCTION_STATUS');
  const { canUpdateCableFeedback } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const debouncedTag = useDebounce(values.tag, 400);
  const [text, setText] = useState<string>('');

  const actualLengthColumn =
    cableTableMetadata && cableTableMetadata.metadataColumns
      ? find(cableTableMetadata.metadataColumns, (item) => item.memberName === 'actualLength')
      : undefined;
  const loadingCheck = feedbackCableLoading || searchCableloading || searchCableReelsLoading;
  const validationCheck =
    values.constructionStatus === ConstructionStatus.CABLE_CUT
      ? !(
          includes(get(values, 'cableReels'), '') ||
          some(values?.cableReels, (obj) => !has(obj, 'reel') || !has(obj, 'reel.id') || isEmpty(get(obj, 'reel.id')))
        )
      : values?.actualLength >= actualLengthColumn?.minValue;

  useEffect(() => {
    if (debouncedTag) {
      searchCables({
        variables: {
          searchInput: {
            filterInput: {
              and: {
                field: 'tag',
                operator: OperatorType.EQUAL,
                value: debouncedTag,
              },
            },
          },
        },
        onCompleted: (data: { searchCables: { items: Cable[] } }) => {
          const cableResult = data?.searchCables?.items[0];
          if (cableResult) {
            searchCableReels({
              variables: {
                searchInput: {
                  filterInput: {
                    and: {
                      field: 'cableId',
                      operator: OperatorType.EQUAL,
                      value: cableResult?.id,
                    },
                  },
                },
              },
            });
            setFieldValue('tagInvalid', false);
          } else {
            setFieldValue('tagInvalid', true);
            setFieldTouched('tag', true, false);
          }
        },
      });
    }
  }, [debouncedTag, searchCables, searchCableReels, setFieldValue, setFieldTouched]);

  useEffect(() => {
    getSelectListData();
  }, [getSelectListData]);

  useEffect(() => {
    if (values.constructionStatus === initialValues.constructionStatus && statusCheck) {
      resetForm({ ...initialValues });
    }
  }, [initialValues, initialValues.cableReels, resetForm, setFieldValue, statusCheck, values.constructionStatus]);

  const defaultSelectedItem = useMemo(() => {
    const constructionStatus = !values.constructionStatus ? ConstructionStatus.CABLE_CUT : values.constructionStatus;

    return defaultTo(find(data?.getSelectListData?.items, { column1: constructionStatus }), constructionStatus);
  }, [data, values.constructionStatus]);

  const handleQuestions = async (questionResponses: QuestionModalResponse[]) => {
    const formikValues = { ...formikValuesRef.current };
    const includesMessageKey = some(questionResponses, {
      messageKey: UiMessageKey.TAG_DOES_NOT_EXIST_DO_YOU_WANT_TO_ADD_IT,
    });

    const tagsWithIndices = map(
      filter(values?.cableReels, (item) => includes(get(item, 'reel.tag', ''), text)),
      (item) => ({
        tag: get(item, 'reel.tag'),
        index: values?.cableReels?.indexOf(item),
      })
    )?.[0];

    if (tagsWithIndices?.tag) {
      questionResponses.forEach((questionResponse) => {
        if (questionResponse.messageKey === UiMessageKey.CABLE_RECUT_FLAG_NOT_SET) {
          formikValues.cableReels = map(formikValues?.cableReels, (cableReel) => ({
            ...cableReel,
            reCut: questionResponse.response,
          }));
        } else if (questionResponse.messageKey === UiMessageKey.TAG_DOES_NOT_EXIST_DO_YOU_WANT_TO_ADD_IT) {
          if (questionResponse.response === Question.REPLY_YES) {
            window.open(
              `../../commodities/reel/new/details?tag=${tagsWithIndices?.tag}&cableCodeId=${cableData?.cableCode?.id}&cableCodeTag=${cableData?.cableCode?.tag}
          `,
              '_blank'
            );
          }
          setIsModalOpen(false);
          setFieldValue(`cableReels[${tagsWithIndices.index}].reel.tag`, '');
          setFieldTouched(`cableReels[${tagsWithIndices.index}].reel.tag`, false);
        }
      });
    }
    if (!includesMessageKey) {
      await saveHandler(formikValues);
    } else {
      setErrorKeysCheck(false);
    }
  };
  const isConstructionStatusReadOnly =
    isEmpty(values.tag) || loadingCheck || !cableData?.constructionStatus || !canUpdateCableFeedback;

  const handleSave = async () => {
    const formErrors = await validateForm();
    if (formErrors) {
      if (Object.keys(formErrors).length > 0) {
        setTouched(setNestedObjectValues(formErrors, true));
        showToast(UiTextEnum.ONE_OR_MORE_FIELDS_ARE_INVALID, [''], ToastType.WARNING);
        return;
      }
    }
    handleSubmit();
  };

  return (
    <>
      <FeedbackMainContainerStyled style={{ overflowY: 'auto' }}>
        <FeedbackCardStyled>
          <FormContainerStyled>
            <Box mb={{ sm: '700' }}>
              <FormField
                isMainCommodity={true}
                memberName="tag"
                required={true}
                pickListName="CABLE_FEEDBACK"
                commodityName={`commodities/cable`}
                displayMemberId="id"
                hasFieldPicklist={true}
              >
                <TextFieldComponent />
              </FormField>
            </Box>
            <FormSectionHeading heading="Status" />
            <Box mb={{ sm: '700' }}>
              <FormField
                alwaysEditable={!isConstructionStatusReadOnly && canUpdateCableFeedback}
                memberName="constructionStatus"
                required={true}
                showPicker={false}
                readOnly={isConstructionStatusReadOnly}
              >
                <SelectFieldComponent
                  items={data?.getSelectListData?.items}
                  selectedComponent={SelectedItemComponent}
                  ItemComponent={ItemComponent}
                  onSelectedItemChange={(changes) => {
                    setFieldValue('constructionStatus', changes.selectedItem.column1);
                    setStatusCheck(true);
                  }}
                  selectedItem={defaultSelectedItem}
                />
              </FormField>
            </Box>
            {values?.constructionStatus !== ConstructionStatus.CABLE_CUT ? (
              <Main loadingCheck={loadingCheck || isEmpty(values.tag)} />
            ) : (
              <CableReels
                cableReelTableMetadata={cableReelTableMetadata}
                loadingCheck={loadingCheck || isEmpty(values.tag) || values.tagInvalid}
                cableData={cableData}
                setIsModalOpen={setIsModalOpen}
                setText={setText}
                setInitialValues={setInitialValues}
                initialValues={initialValues}
              />
            )}
          </FormContainerStyled>
        </FeedbackCardStyled>
      </FeedbackMainContainerStyled>
      {(isModalOpen || (errorKeysCheck && errorKeys && errorKeys.length > 0)) && (
        <MessageModal
          messageKeys={isModalOpen ? [UiMessageKey.TAG_DOES_NOT_EXIST_DO_YOU_WANT_TO_ADD_IT] : errorKeys}
          onSubmit={handleQuestions}
        ></MessageModal>
      )}
      <CommodityFooter
        saveHandler={handleSave}
        loading={isSubmitting}
        disableSave={!canUpdateCableFeedback || !dirty || !touched || loadingCheck || !validationCheck}
        disableCancel={!dirty || !touched || loadingCheck}
        cancelHandler={() => handleReset()}
      />
    </>
  );
};

export default CableFeedbackForm;
